<script setup lang="ts">
import { ErrorMessage, Field as VeeField } from 'vee-validate';
import { ref } from 'vue';
import BackstageInputLabel from './BackstageInputLabel.vue';

export interface Props {
  name?: string;
  label?: string;
  modelValue?: string;
  required?: boolean;
  disabled?: boolean;
  dataCy?: string;
}

withDefaults(defineProps<Props>(), {
  name: '',
  label: '',
  modelValue: '',
  required: false,
  disabled: false,
});

const emit = defineEmits<{
  change: [];
  'update:modelValue': [value: string | number];
}>();

const touched = ref(false);
const inputFocused = ref(false);
const textarea = ref<HTMLInputElement | null>(null);

// methods
function focusOnInput() {
  inputFocused.value = true;
  textarea.value?.focus();
}

function removeInputFocus() {
  inputFocused.value = false;
  emit('change');
}

function updateValue(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  emit('update:modelValue', value);
}
</script>

<template>
  <span>
    <BackstageInputLabel
      :label="label"
      :required="required"
      :class="{ disabled: disabled }"
      v-on:focus="focusOnInput"
      v-on:click="focusOnInput">
      <VeeField
        v-if="name"
        :name="name"
        v-slot="{ field }"
        :model-value="modelValue"
        :validate-on-input="true"
        @blur="touched = true">
        <textarea
          v-bind="{ ...$attrs, ...field }"
          ref="textarea"
          :id="name"
          :name="name"
          :value="modelValue"
          :disabled="disabled"
          :data-cy="dataCy"
          v-on:blur="removeInputFocus"
          v-on:focus="focusOnInput"
          v-on:input="updateValue">
          <slot></slot>
        </textarea>
      </VeeField>
      <textarea
        v-else
        ref="textarea"
        :data-cy="dataCy"
        :value="modelValue"
        :disabled="disabled"
        v-bind="$attrs"
        v-on:blur="removeInputFocus"
        v-on:focus="focusOnInput"
        v-on:input="updateValue">
        <slot></slot>
      </textarea>
    </BackstageInputLabel>
    <ErrorMessage v-if="name && touched" :name="name" />
  </span>
</template>

<style lang="scss" scoped>
.field-new:deep() {
  .input-container {
    flex: 1;

    textarea {
      width: 100%;
      height: 100%;
      max-height: auto;
    }
  }
}
</style>
